import React, { useState } from 'react';
import { Check, Users, Minus, Plus, StickyNote } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

// ---------------------------------------------------------------------------
// Shared / team goal UI bits — roster (who ticked + when + note), avatar stack,
// and the note-capturing tick controls for both the goal rows and habit grid.
// ---------------------------------------------------------------------------
const AV_TONES = [
  'bg-rose-500', 'bg-amber-500', 'bg-emerald-500', 'bg-sky-500',
  'bg-violet-500', 'bg-fuchsia-500', 'bg-teal-500', 'bg-indigo-500',
];

function initials(name) {
  return (name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
}
function toneFor(key) {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_TONES[h % AV_TONES.length];
}
function tickTime(at) {
  if (!at) return '';
  const d = new Date(at);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `Today ${t}` : `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${t}`;
}

/* Overlapping avatar stack for the members who ticked a period. */
export const TickerAvatars = ({ roster = [], max = 4, size = 20 }) => {
  if (!roster.length) return null;
  const shown = roster.slice(0, max);
  const extra = roster.length - shown.length;
  return (
    <div className="flex items-center" data-testid="planner-goal-ticker-avatars">
      {shown.map((r, i) => (
        <span
          key={r.user_id || i}
          title={r.name}
          className={`grid place-items-center rounded-full text-white font-semibold ring-2 ring-[color:var(--p-card)] ${toneFor(r.user_id || r.name)}`}
          style={{ width: size, height: size, fontSize: size * 0.42, marginLeft: i ? -size * 0.3 : 0, zIndex: shown.length - i }}
        >
          {initials(r.name)}
        </span>
      ))}
      {extra > 0 && (
        <span className="grid place-items-center rounded-full bg-[color:var(--p-card-2)] text-[color:var(--p-text-soft)] font-semibold ring-2 ring-[color:var(--p-card)]"
          style={{ width: size, height: size, fontSize: size * 0.4, marginLeft: -size * 0.3 }}>
          +{extra}
        </span>
      )}
    </div>
  );
};

/* Non-interactive avatar stack for a shared event / meeting — shows who the
   item is shared with (owner + recipients / attendees) "at a glance".
   Renders only spans/divs, so it is safe to nest inside a <button> block.
   A native title tooltip lists everyone. */
export const ParticipantAvatars = ({ item, size = 15, max = 3 }) => {
  if (!item) return null;
  const isMeeting = item.kind === 'meeting';
  const people = isMeeting ? (item.attendees || []) : (item.shared_with || []);
  if (!people.length) return null;
  const ownerName = item.is_owner ? 'You' : (item.owner_name || item.shared_by || 'Owner');
  const roster = [];
  const seen = new Set();
  if (item.owner_id && !seen.has(item.owner_id)) { roster.push({ user_id: item.owner_id, name: ownerName }); seen.add(item.owner_id); }
  for (const p of people) { if (p && p.id && !seen.has(p.id)) { roster.push({ user_id: p.id, name: p.name }); seen.add(p.id); } }
  if (!roster.length) return null;
  const title = `${isMeeting ? 'Attendees' : 'Shared with'}: ${roster.map((r) => r.name).join(', ')}`;
  return (
    <span className="inline-flex shrink-0" title={title} data-testid="planner-shared-avatars">
      <TickerAvatars roster={roster} max={max} size={size} />
    </span>
  );
};

/* "Who ticked" list with each member's time + optional note. */
export const RosterList = ({ roster = [], emptyLabel = 'No one has ticked yet.' }) => {
  if (!roster.length) {
    return <div className="py-3 text-center text-[11.5px] text-[color:var(--p-muted)]" data-testid="planner-goal-roster-empty">{emptyLabel}</div>;
  }
  return (
    <div className="space-y-2" data-testid="planner-goal-roster">
      {roster.map((r, i) => (
        <div key={r.user_id || i} className="flex items-start gap-2" data-testid={`planner-goal-roster-item-${r.user_id || i}`}>
          <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-white text-[10px] font-semibold ${toneFor(r.user_id || r.name)}`}>
            {initials(r.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[12px] font-medium text-[color:var(--p-text)]">{r.name}</span>
              <span className="shrink-0 text-[10px] text-[color:var(--p-muted-2)]">{tickTime(r.at)}</span>
            </div>
            {r.note ? (
              <div className="mt-0.5 flex items-start gap-1 rounded-md bg-[color:var(--p-card-2)] px-2 py-1 text-[11.5px] text-[color:var(--p-text-soft)]">
                <StickyNote size={11} className="mt-0.5 shrink-0 opacity-70" />
                <span className="whitespace-pre-wrap break-words">{r.note}</span>
              </div>
            ) : (
              <div className="text-[10.5px] italic text-[color:var(--p-muted-2)]">No note</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* Note composer shown inside a tick popover (only when marking done / logging). */
const TickNoteComposer = ({ onConfirm, label = 'Mark done', icon: Icon = Check }) => {
  const [note, setNote] = useState('');
  return (
    <div className="space-y-2">
      <Textarea
        rows={2} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Add an optional note for your team…"
        className="resize-none bg-[color:var(--p-card)] text-[color:var(--p-text)] border-[color:var(--p-border)] text-[12px]"
        data-testid="planner-goal-tick-note-input"
      />
      <Button type="button" onClick={() => onConfirm(note.trim())}
        className="w-full bg-[var(--p-accent)] text-white hover:bg-[var(--p-accent-2)]"
        data-testid="planner-goal-tick-confirm">
        <Icon size={14} className="mr-1" /> {label}
      </Button>
    </div>
  );
};

/* Per-entry log list for a QUANTITY goal (who logged, when, with optional note). */
const LogList = ({ logs = [] }) => {
  if (!logs.length) {
    return <div className="py-3 text-center text-[11.5px] text-[color:var(--p-muted)]" data-testid="planner-qty-logs-empty">No one has logged any yet.</div>;
  }
  return (
    <div className="space-y-2" data-testid="planner-qty-logs">
      {logs.map((r, i) => (
        <div key={(r.user_id || '') + i} className="flex items-start gap-2" data-testid={`planner-qty-log-item-${i}`}>
          <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-white text-[10px] font-semibold ${toneFor(r.user_id || r.name)}`}>
            {initials(r.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[12px] font-medium text-[color:var(--p-text)]">{r.name}</span>
              <span className="shrink-0 text-[10px] text-[color:var(--p-muted-2)]">{tickTime(r.at)}</span>
            </div>
            {r.note ? (
              <div className="mt-0.5 flex items-start gap-1 rounded-md bg-[color:var(--p-card-2)] px-2 py-1 text-[11.5px] text-[color:var(--p-text-soft)]">
                <StickyNote size={11} className="mt-0.5 shrink-0 opacity-70" />
                <span className="whitespace-pre-wrap break-words">{r.note}</span>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

/* Counter control for a QUANTITY goal (e.g. "2 creatives / day").
   − removes the viewer's most recent log, + logs one more (optionally with a
   note). The count pill opens a "logged by" popover for shared goals. */
export const QuantityGoalControl = ({ goal, onQuantity }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const target = goal.target || goal.quantity_target || 1;
  const count = goal.period_count ?? 0;
  const myCount = goal.my_count ?? count;
  const met = !!goal.period_met;
  const shared = !!goal.is_shared;
  const teamMode = goal.count_mode === 'team';
  const logs = goal.logs || [];

  const inc = (note) => onQuantity && onQuantity(goal, 'inc', note);
  const dec = () => onQuantity && onQuantity(goal, 'dec');

  const countPill = (
    <span
      className={`p-tnum inline-flex min-w-[46px] items-center justify-center gap-0.5 rounded-full border px-2 py-0.5 text-[12px] font-semibold ${
        met ? 'border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] text-[color:var(--p-accent)]'
            : 'border-[color:var(--p-border)] bg-[color:var(--p-card-2)] text-[color:var(--p-text-soft)]'}`}
      data-testid="planner-qty-count">
      {count}<span className="opacity-60">/{target}</span>
    </span>
  );

  return (
    <div className="flex items-center gap-1" data-testid="planner-qty-control">
      <button
        type="button" onClick={dec} disabled={myCount <= 0}
        aria-label="Remove one of yours"
        data-testid="planner-qty-dec"
        className="n-focus grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md border border-[color:var(--p-border)] bg-[color:var(--p-card)] text-[color:var(--p-text-soft)] transition-colors hover:border-[var(--p-accent)] disabled:cursor-not-allowed disabled:opacity-40">
        <Minus size={13} strokeWidth={2.6} />
      </button>

      {shared ? (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="n-focus cursor-pointer" aria-label="See who logged" data-testid="planner-qty-count-trigger">
              {countPill}
            </button>
          </PopoverTrigger>
          <PopoverContent className="notes-scope w-[280px] p-3" align="center" data-testid="planner-qty-logs-popover">
            <div className="mb-1.5 flex items-center justify-between">
              <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--p-muted)]">
                <Users size={11} /> Logged by
              </div>
              <span className="text-[10.5px] text-[color:var(--p-muted-2)]">{teamMode ? 'Whole team' : 'Per member'}</span>
            </div>
            <LogList logs={logs} />
          </PopoverContent>
        </Popover>
      ) : countPill}

      <button
        type="button" onClick={() => inc('')}
        aria-label="Log one"
        data-testid="planner-qty-inc"
        className="n-focus grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md border border-[var(--p-accent)] bg-[var(--p-accent)] text-white transition-colors hover:bg-[var(--p-accent-2)]">
        <Plus size={13} strokeWidth={2.8} />
      </button>

      <Popover open={noteOpen} onOpenChange={setNoteOpen}>
        <PopoverTrigger asChild>
          <button
            type="button" aria-label="Log one with a note"
            data-testid="planner-qty-note-add"
            className="n-focus grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md border border-[color:var(--p-border)] bg-[color:var(--p-card)] text-[color:var(--p-text-soft)] transition-colors hover:border-[var(--p-accent)]">
            <StickyNote size={12} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="notes-scope w-[260px] p-3" align="end" data-testid="planner-qty-note-popover">
          <TickNoteComposer label="Log one" icon={Plus} onConfirm={(n) => { inc(n); setNoteOpen(false); }} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

/* Checkbox + popover for a SHARED classic goal (daily/weekly/monthly).
   Reflects the viewer's OWN tick; popover captures an optional note on tick,
   lets them remove their tick, and always shows the team roster. */
export const SharedGoalTick = ({ goal, onToggle }) => {
  const [open, setOpen] = useState(false);
  const mine = !!goal.my_completed;
  const confirm = (note) => { onToggle && onToggle(goal, note); setOpen(false); };
  const remove = () => { onToggle && onToggle(goal); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="planner-goal-toggle-checkbox"
          aria-label={mine ? 'Edit your tick' : 'Tick for today'}
          aria-pressed={mine}
          className={`n-focus mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[6px] border transition-colors ${
            mine ? 'border-[var(--p-accent)] bg-[var(--p-accent)] text-white'
                 : 'border-[var(--p-muted-2)] bg-[var(--p-card)] hover:border-[var(--p-accent)]'}`}
        >
          {mine && <Check size={13} strokeWidth={3} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="notes-scope w-[280px] p-3" align="start" data-testid="planner-goal-tick-popover">
        {mine ? (
          <Button type="button" variant="outline" onClick={remove}
            className="w-full border-[color:var(--p-border)] text-[color:var(--p-text-soft)]"
            data-testid="planner-goal-tick-remove">
            <Minus size={14} className="mr-1" /> Remove my tick
          </Button>
        ) : (
          <TickNoteComposer onConfirm={confirm} />
        )}
        <div className="my-2.5 border-t border-[color:var(--p-line)]" />
        <div className="mb-1.5 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--p-muted)]">
          <Users size={11} /> Ticked by
        </div>
        <RosterList roster={goal.roster} />
      </PopoverContent>
    </Popover>
  );
};

/* One habit-grid cell for a SHARED goal: team-based done state, shows whether
   the viewer personally ticked, and a popover with note capture + roster. */
export const SharedHabitCell = ({ cell, onToggle, size = 27 }) => {
  const [open, setOpen] = useState(false);
  const { state, date } = cell;
  const tickable = state === 'done' || state === 'miss' || state === 'today';
  const mine = !!cell.mine;
  const roster = cell.tickers || [];
  const confirm = (note) => { onToggle && onToggle(date, note); setOpen(false); };
  const remove = () => { onToggle && onToggle(date); setOpen(false); };

  const inner = (
    <>
      {state === 'done' && (mine ? <Check size={14} strokeWidth={3.2} /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />)}
      {state === 'miss' && <i />}
      {state === 'soon' && <i />}
      {state === 'oor' && <Minus size={12} strokeWidth={2.5} />}
    </>
  );

  if (!tickable) {
    return (
      <button type="button" disabled data-testid={`planner-habit-tick-${date}`}
        className={`p-tick p-tick-${state} cursor-default`} style={{ width: size, height: size }} aria-hidden>
        {inner}
      </button>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`planner-habit-tick-${date}`}
          aria-label={`${roster.length} ticked on ${date}`}
          aria-pressed={state === 'done'}
          className={`n-focus p-tick p-tick-${state} cursor-pointer ${mine ? 'ring-2 ring-[color:var(--p-accent)] ring-offset-1 ring-offset-[color:var(--p-card)]' : ''}`}
          style={{ width: size, height: size }}
        >
          {inner}
        </button>
      </PopoverTrigger>
      <PopoverContent className="notes-scope w-[260px] p-3" align="center" data-testid="planner-habit-tick-popover">
        {mine ? (
          <Button type="button" variant="outline" onClick={remove}
            className="w-full border-[color:var(--p-border)] text-[color:var(--p-text-soft)]"
            data-testid="planner-habit-tick-remove">
            <Minus size={14} className="mr-1" /> Remove my tick
          </Button>
        ) : (
          <TickNoteComposer onConfirm={confirm} />
        )}
        <div className="my-2.5 border-t border-[color:var(--p-line)]" />
        <div className="mb-1.5 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--p-muted)]">
          <Users size={11} /> Ticked this day
        </div>
        <RosterList roster={roster} />
      </PopoverContent>
    </Popover>
  );
};

/* Small inline "shared" marker for goal rows / habit row titles. */
export const SharedBadge = ({ count }) => (
  <span className="inline-flex items-center gap-0.5 rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-accent-bg)] px-1.5 py-0.5 text-[9.5px] font-semibold text-[color:var(--p-accent)]"
    title={`Team goal${count ? ` · ${count} members` : ''}`} data-testid="planner-goal-shared-badge">
    <Users size={10} /> Team
  </span>
);

/* Clickable avatar stack that opens a popover with the full roster + notes, so
   anyone can read who ticked / logged this period and the note they left.
   Pass ``logs`` for quantity goals (per-entry list) or rely on ``roster``
   (per-member list) for classic shared goals. */
export const RosterAvatars = ({ roster = [], logs = null, title = 'Ticked by', max = 4, size = 20 }) => {
  if (!roster.length && !(logs && logs.length)) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="n-focus cursor-pointer rounded-full"
          aria-label={`${title} — view notes`}
          data-testid="planner-goal-roster-trigger"
        >
          <TickerAvatars roster={roster.length ? roster : logs} max={max} size={size} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="notes-scope w-[280px] p-3" align="end" data-testid="planner-goal-roster-popover">
        <div className="mb-1.5 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--p-muted)]">
          <Users size={11} /> {title}
        </div>
        {logs ? <LogList logs={logs} /> : <RosterList roster={roster} />}
      </PopoverContent>
    </Popover>
  );
};
